import { ScrollView, Alert } from "react-native";
import { View, Text } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { Stack, useRouter } from "expo-router";
import { useFocusEffect } from "@react-navigation/native";
import { useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { HeaderBackButton } from "@react-navigation/elements";
import { Icon } from "@src/components/Icon";
import { useCameraDetailsData } from "@src/features/cameras/hooks/useCameraDetailsData";
import { useCameraAlarmPresentation } from "@src/features/cameras/hooks/useCameraAlarmPresentation";
import { useAlarmOverridePanelState, useSubmitAlarmOverride } from "@src/features/cameras/hooks";
import {
  ArmDisarmControl,
  AdjustControl,
  SettingsCameraItem,
  ScrollableBottomSheet,
  CameraSnapshot,
} from "@src/components/CameraDetails";
import { ArmDisarmPanelContent } from "@visioni/a3s-alarm-schedules/app";
import { TimePickerWith24Hour } from "@src/components/TimePickerWith24Hour";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useDayClipCounts, ActivityLastDayHeatmap } from "@src/features/clips";
import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import SilenceAlarmBottomSheet from "@src/components/CameraDetails/SilenceAlarmBottomSheet";
import { useUpdateAlarmOverride, useUpdateAlarmSilence } from "@src/services/api/hooks";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { toZonedTime } from "date-fns-tz";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { jsDateToDateTime, dateTimeToDateTimeRecord, dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { getCameraStreamingCapability } from "@src/utils/camera-capabilities";
import { usePeriodicRefresh } from "@src/hooks/usePeriodicRefresh";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";
import { useSnapshots } from "@visioni/a3s-media/app";

export default function CameraDetailsScreen() {
  const {
    tenantId,
    siteId,
    cameraKey,
    effectiveSiteId,
    effectiveCamera,
    isCameraLoading,
    isCameraFetching,
    cameraError,
    refetchCamera,
    alarmDetail,
    isAlarmLoading,
    isAlarmFetching,
    alarmError,
    refetchAlarm,
    isLegacyAppCamera,
  } = useCameraDetailsData();

  const { data: snapshots } = useSnapshots({});
  const { theme } = useUnistyles();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { track } = useAmplitude();
  // Periodically trigger re-computation of armed/disarmed state
  const currentTime = usePeriodicRefresh();

  // Fetch alarm details for this camera
  // Mutations for override and silence
  const updateOverrideMutation = useUpdateAlarmOverride();
  const updateSilenceMutation = useUpdateAlarmSilence();

  // Fetch clips for today to show activity summary
  const cameraTimezone = useMemo(
    () => getTimezoneFromAlarmDetail(alarmDetail) || Intl.DateTimeFormat().resolvedOptions().timeZone,
    [alarmDetail],
  );
  const today = useMemo(() => toZonedTime(new Date(), cameraTimezone), [cameraTimezone]);
  const { hourBuckets: todayHourBuckets, isLoading: isClipsLoading } = useDayClipCounts({
    siteId: effectiveSiteId,
    cameraKey: cameraKey ?? "",
    selectedDate: today,
    timezone: cameraTimezone,
  });

  // Calculate total clips for today
  const totalClipsToday = useMemo(() => {
    return todayHourBuckets.reduce((sum, bucket) => sum + bucket.count, 0);
  }, [todayHourBuckets]);

  const armDisarmBottomSheetRef = useRef<BottomSheetModal>(null);
  const silenceAlarmBottomSheetRef = useRef<BottomSheetModal>(null);
  const [isAdjust, setIsAdjust] = useState(false);
  // Check if alarm is configured (has settings)
  const isAlarmConfigured = useMemo(() => {
    return !!alarmDetail?.settings;
  }, [alarmDetail]);

  // Ensure bottom sheets are closed whenever this screen gains focus
  useFocusEffect(
    useCallback(() => {
      armDisarmBottomSheetRef.current?.dismiss();
      silenceAlarmBottomSheetRef.current?.dismiss();
    }, []),
  );

  // Ensure bottom sheets are closed on initial mount after refs attach
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      armDisarmBottomSheetRef.current?.dismiss();
      silenceAlarmBottomSheetRef.current?.dismiss();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const { armedState, isArmed, statusText, scheduleTime } = useCameraAlarmPresentation({ alarmDetail, currentTime });

  // Check if alarm is currently silenced
  const isSilenced = useMemo(() => {
    if (!alarmDetail?.settings?.silence) {
      return false;
    }
    const silence = alarmDetail.settings.silence;
    const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
    const now = new Date();
    const nowDateTime = jsDateToDateTime(now).setZone(timezone);

    // Check if current time is within silence period
    // Use dateTimeRecordToLocalisedDateTime to properly handle timezone conversion
    const startDateTime = dateTimeRecordToLocalisedDateTime(silence.period.start, timezone);
    const endDateTime = dateTimeRecordToLocalisedDateTime(silence.period.end, timezone);

    // Compare in milliseconds
    const nowMs = nowDateTime.toMillis();
    const startMs = startDateTime.toMillis();
    const endMs = endDateTime.toMillis();

    const result = nowMs >= startMs && nowMs < endMs;
    return result;
  }, [alarmDetail]);

  // Get silence remaining minutes
  const silenceRemainingMinutes = useMemo(() => {
    if (!isSilenced || !alarmDetail?.settings?.silence) return 0;
    const silence = alarmDetail.settings.silence;
    const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
    const endDateTime = dateTimeRecordToLocalisedDateTime(silence.period.end, timezone);
    const nowDateTime = jsDateToDateTime(new Date()).setZone(timezone);
    const diffMs = endDateTime.toMillis() - nowDateTime.toMillis();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60)));
  }, [isSilenced, alarmDetail]);

  // Get snapshot URL from camera data
  const snapshotUrl = useMemo(() => {
    return effectiveCamera?.snapshot?.url ?? null;
  }, [effectiveCamera]);
  const snapshotCapturedAt = useMemo(() => {
    return effectiveCamera?.snapshot?.capturedAt ?? null;
  }, [effectiveCamera]);

  // Parse camera key to extract asset part (before @) and camera ID part (after @)
  const cameraAssetPart = useMemo(() => {
    if (!cameraKey) return "Camera";
    const parts = cameraKey.split("@");
    return parts.length > 0 ? parts[0] : cameraKey;
  }, [cameraKey]);

  const cameraIdPart = useMemo(() => {
    if (!cameraKey) return "";
    const parts = cameraKey.split("@");
    return parts.length > 1 ? parts[1] : "";
  }, [cameraKey]);

  const isLoading = isLegacyAppCamera ? false : isCameraLoading || isAlarmLoading;
  const isFetching = isLegacyAppCamera ? false : isCameraFetching || isAlarmFetching;
  const error = isLegacyAppCamera ? null : cameraError || alarmError;
  const refetch = () => {
    refetchCamera?.();
    refetchAlarm();
  };

  const isActivated = useMemo(() => {
    if (!effectiveCamera) return false;
    return effectiveCamera.alarmStatus?.status === "active";
  }, [effectiveCamera]);

  const cameraRouteBase = useMemo(() => {
    if (!tenantId || !effectiveSiteId || !cameraKey) return null;
    return `/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(effectiveSiteId)}/cameras/${encodeURIComponent(
      cameraKey,
    )}`;
  }, [tenantId, effectiveSiteId, cameraKey]);

  const handleAlarmSettingsPress = useCallback(() => {
    if (!cameraRouteBase) return;
    router.push(`${cameraRouteBase}/alarm` as never);
  }, [router, cameraRouteBase]);

  const handleNotificationSettingsPress = useCallback(() => {
    if (!cameraKey) return;

    track(ANALYTICS_EVENTS.CAMERA_NOTIFICATION_SETTINGS_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: effectiveSiteId,
    });
    if (!cameraRouteBase) return;
    router.push(`${cameraRouteBase}/activity-notification` as never);
  }, [router, tenantId, effectiveSiteId, cameraKey, track, cameraRouteBase]);

  const handleManageAccessPress = useCallback(() => {
    router.push({
      pathname: "/contacts/manage-access",
      params: {
        cameraNickname: effectiveCamera?.nickname ?? "",
        cameraAssetPart,
        cameraIdPart,
        siteId: effectiveSiteId,
        cameraKey: cameraKey ?? "",
      },
    });
  }, [router, effectiveCamera?.nickname, cameraAssetPart, cameraIdPart, effectiveSiteId, cameraKey]);

  const handleCameraDetailsPress = useCallback(() => {
    if (!cameraRouteBase) return;
    router.push(`${cameraRouteBase}/details` as never);
  }, [router, cameraRouteBase]);

  const handleActivityAlertsPress = useCallback(() => {
    track(ANALYTICS_EVENTS.CAMERA_VIEW_CLIPS_CLICKED, {
      camera_id: cameraKey,
      tenant_id: tenantId,
      site_id: effectiveSiteId,
    });
    if (!cameraRouteBase) return;
    router.push(`${cameraRouteBase}/clips` as never);
  }, [router, cameraKey, effectiveSiteId, tenantId, track, cameraRouteBase]);

  const handlePlayPress = useCallback(() => {
    if (!cameraRouteBase || !effectiveCamera) return;

    const streamType = getCameraStreamingCapability(effectiveCamera);

    if (streamType === "jpeg") {
      track(ANALYTICS_EVENTS.JPEG_STREAM_VIEWED, {
        camera_id: cameraKey,
        tenant_id: tenantId,
        site_id: effectiveSiteId,
        stream_type: "jpeg",
      });
      router.push(`${cameraRouteBase}/livestream` as never);
    } else {
      track(ANALYTICS_EVENTS.CAMERA_VIEW_LIVESTREAM_CLICKED, {
        camera_id: cameraKey,
        tenant_id: tenantId,
        site_id: effectiveSiteId,
        stream_type: "mp4",
      });
      router.push(`${cameraRouteBase}/livestream` as never);
    }
  }, [router, tenantId, effectiveSiteId, cameraKey, track, cameraRouteBase, effectiveCamera]);

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.push("/overview");
  }, [router]);

  const headerLeft = useCallback(
    () => <HeaderBackButton onPress={handleBackPress} tintColor={theme.colors.text} />,
    [handleBackPress, theme.colors.text],
  );

  // Opens the bottom sheet to select time duration
  const handleArmDisarm = useCallback(() => {
    setIsAdjust(false);
    armDisarmBottomSheetRef.current?.present();
  }, []);

  // Opens the bottom sheet in adjust mode to modify an existing override
  const handleAdjustOverride = useCallback(() => {
    setIsAdjust(true);
    armDisarmBottomSheetRef.current?.present();
  }, []);

  const bottomSheetMode = useMemo<"override" | "adjust">(() => (isAdjust ? "adjust" : "override"), [isAdjust]);
  const { activeOverride: bottomSheetActiveOverride, targetAction: bottomSheetTargetAction } =
    useAlarmOverridePanelState({
      mode: bottomSheetMode,
      armedState,
      isArmed,
    });

  const { submitOverride: handleArmDisarmConfirm } = useSubmitAlarmOverride({
    mode: bottomSheetMode,
    alarmDetail,
    cameraKey,
    siteId: effectiveSiteId,
    requireSchedule: true,
    onSuccess: ({ mode, override }) => {
      track(ANALYTICS_EVENTS.ALARM_OVERRIDE_SAVED, {
        camera_id: cameraKey,
        action: mode === "adjust" ? "extended" : "created",
        override_action: override.action,
        tenant_id: tenantId,
        site_id: effectiveSiteId,
      });
      if (mode === "adjust") {
        showSuccessToast({ message: "Alarm extended", description: "Duration extended" });
      } else {
        const actionText = override.action === "armed" ? "armed" : "disarmed";
        showSuccessToast({ message: `Alarm ${actionText}`, description: "Override applied" });
      }
    },
  });

  const handleAdjust = useCallback(() => {
    // If there's an active override, show confirmation before removing it
    if (armedState?.isInOverride) {
      if (!cameraKey || !alarmDetail) return;

      const overrideAction = armedState.activeOverride?.action;
      const actionText = overrideAction === "armed" ? "arm" : "disarm";

      Alert.alert("Resume schedule", `Are you sure you want to remove your manual override to ${actionText}?`, [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Resume schedule",
          style: "default",
          onPress: () => {
            const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";

            updateOverrideMutation.mutate(
              { siteId: effectiveSiteId, cameraKey, override: null, timezone },
              {
                onSuccess: () => {
                  track(ANALYTICS_EVENTS.ALARM_OVERRIDE_SAVED, {
                    camera_id: cameraKey,
                    action: "removed",
                    tenant_id: tenantId,
                    site_id: effectiveSiteId,
                  });
                  showSuccessToast({ message: "Schedule resumed", description: "Override removed" });
                },
                onError: (err: unknown) => {
                  showErrorToast({ error: err });
                  console.error("Override removal error:", err);
                },
              },
            );
          },
        },
      ]);
    } else {
      // No active override, open bottom sheet to create one
      setIsAdjust(true);
      armDisarmBottomSheetRef.current?.present();
    }
  }, [armedState, cameraKey, alarmDetail, updateOverrideMutation, track, tenantId, effectiveSiteId]);

  const handleSilenceAlarm = useCallback(() => {
    silenceAlarmBottomSheetRef.current?.present();
  }, []);

  // Handle silence confirmation from bottom sheet
  const handleSilenceConfirm = useCallback(
    (minutes: number) => {
      if (!cameraKey || !alarmDetail) return;

      const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
      const nowDateTime = jsDateToDateTime(new Date()).setZone(timezone);

      // If already silenced, extend from current end time; otherwise start from now
      let startDateTime;
      let endDateTime;

      if (isSilenced && alarmDetail?.settings?.silence) {
        // Extend existing silence - keep original start, extend end by adding minutes
        const currentSilence = alarmDetail.settings.silence;

        // Parse dates in the correct timezone
        const currentStartDateTime = dateTimeRecordToLocalisedDateTime(currentSilence.period.start, timezone);
        const currentEndDateTime = dateTimeRecordToLocalisedDateTime(currentSilence.period.end, timezone);

        // Keep original start time, extend end by adding minutes
        startDateTime = currentStartDateTime;
        endDateTime = currentEndDateTime.plus({ minutes });
      } else {
        // New silence - start from now
        startDateTime = nowDateTime;
        endDateTime = nowDateTime.plus({ minutes });
      }

      const silence = {
        id: `silence-${Date.now()}`,
        reason: isSilenced ? `Extended silence by ${minutes} minutes` : `Silenced for ${minutes} minutes`,
        period: {
          start: dateTimeToDateTimeRecord(startDateTime),
          end: dateTimeToDateTimeRecord(endDateTime),
        },
      };

      updateSilenceMutation.mutate(
        { siteId: effectiveSiteId, cameraKey, silence, timezone },
        {
          onSuccess: () => {
            track(ANALYTICS_EVENTS.ALARM_SILENCE_SAVED, {
              camera_id: cameraKey,
              action: isSilenced ? "extended" : "created",
              minutes,
              tenant_id: tenantId,
              site_id: effectiveSiteId,
            });
            const message = isSilenced ? "Silence extended" : "Alarm silenced";
            showSuccessToast({ message: message, description: `Silenced for ${minutes} minutes` });
          },
          onError: (err: unknown) => {
            showErrorToast({ error: err });
            console.error("Silence mutation error:", err);
          },
        },
      );
    },
    [cameraKey, alarmDetail, updateSilenceMutation, isSilenced, track, tenantId, effectiveSiteId],
  );

  // Header title component - must be defined before early returns to maintain hook order
  // Show nickname if available, otherwise use two-part style (asset part + camera ID part) like alarms tab
  const headerTitle = useCallback(
    () => (
      <View flexDirection="row" alignItems="center" gap="xs">
        {effectiveCamera?.nickname ?
          <>
            <Text textStyle="md/medium">{effectiveCamera.nickname}</Text>
            {cameraIdPart && (
              <Text textStyle="md/medium" color="textMuted">
                {cameraIdPart}
              </Text>
            )}
          </>
        : <>
            <Text textStyle="md/medium">{cameraAssetPart}</Text>
            {cameraIdPart && (
              <Text textStyle="md/medium" color="textMuted">
                {cameraIdPart}
              </Text>
            )}
          </>
        }
      </View>
    ),
    [effectiveCamera?.nickname, cameraAssetPart, cameraIdPart],
  );

  // Header options based on state
  const headerOptions = useMemo(() => {
    const baseOptions = {
      headerStyle: {
        backgroundColor: theme.colors.background,
      },
      headerTintColor: theme.colors.text,
      headerBackButtonDisplayMode: "minimal" as const,
      headerLeft,
    };

    if (isLoading || !effectiveCamera) {
      return {
        ...baseOptions,
        title: isLoading ? "Camera" : "Camera Not Found",
      };
    }

    return {
      ...baseOptions,
      headerTitle,
    };
  }, [theme.colors.background, theme.colors.text, isLoading, effectiveCamera, headerTitle, headerLeft]);

  // Render camera content
  const renderCameraContent = () => (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.colors.background }}
      contentContainerStyle={{
        paddingBottom: insets.bottom + theme.spacing["3xl"],
      }}
    >
      {/* Camera Snapshot */}
      <View mt="5xl">
        <CameraSnapshot
          snapshotUrl={snapshotUrl}
          snapshotCapturedAt={snapshotCapturedAt}
          onPlayPress={handlePlayPress}
          isOnline={effectiveCamera?.isOnline}
          cameraKey={cameraKey}
          signedSnapshot={cameraKey ? snapshots?.[cameraKey] : undefined}
        />
      </View>

      {/* Arm/Disarm Section */}
      <View px="5xl" mt="xl">
        <ArmDisarmControl
          isArmed={isArmed}
          isActivated={isActivated}
          onArmDisarm={handleArmDisarm}
          onAdjustOverride={handleAdjustOverride}
          onResumeSchedule={handleAdjust}
          statusText={statusText}
          onSilenceAlarm={handleSilenceAlarm}
          isSilenced={isSilenced}
          silencedMinutes={silenceRemainingMinutes}
          hasActiveOverride={armedState?.isInOverride ?? false}
          overrideAction={armedState?.activeOverride?.action}
        />
      </View>

      {/* Adjust Section */}
      <FeatureGate flag={FEATURE_FLAGS.CAMERA_ALARM_ADJUST}>
        <View px="5xl" mt="sm">
          <AdjustControl
            scheduleTime={scheduleTime}
            onAdjust={handleAdjust}
            onAdjustOverride={handleAdjustOverride}
            statusText={statusText}
            hasActiveOverride={armedState?.isInOverride ?? false}
            overrideAction={armedState?.activeOverride?.action}
          />
        </View>
      </FeatureGate>

      <View px="5xl" mt="3xl">
        <Text textStyle="xs/medium" style={{ color: theme.colors.interactive.controls.default_midground }}>
          Camera settings
        </Text>
        <SettingsCameraItem
          icon={<Icon name="activity" size={18} color={theme.colors.interactive.controls.default_midground} />}
          subtitle={isClipsLoading ? "Loading..." : `${totalClipsToday} clips past 24 hrs`}
          title="Clips"
          onPress={handleActivityAlertsPress}
          additionalSection={
            <Animated.View entering={FadeIn} exiting={FadeOut} key={isClipsLoading ? "loading" : "loaded"}>
              <ActivityLastDayHeatmap siteId={effectiveSiteId} cameraKey={cameraKey ?? ""} timezone={cameraTimezone} />
            </Animated.View>
          }
        />
        <SettingsCameraItem
          icon={<Icon name="calendarOutline" size={18} color={theme.colors.interactive.controls.default_midground} />}
          title="Alarm settings"
          subtitle="Schedule Operating"
          onPress={handleAlarmSettingsPress}
        />
        <FeatureGate flag={FEATURE_FLAGS.USERS_PERMISSIONS_OPTION}>
          <SettingsCameraItem
            icon={<Icon name="users" size={18} color={theme.colors.interactive.controls.default_midground} />}
            title="Users & permissions"
            onPress={handleManageAccessPress}
          />
        </FeatureGate>
        <SettingsCameraItem
          icon={<Icon name="bellOutline" size={18} color={theme.colors.interactive.controls.default_midground} />}
          title="Notification settings"
          onPress={handleNotificationSettingsPress}
        />
        <FeatureGate flag={FEATURE_FLAGS.CAMERA_DETAILS_OPTION}>
          <SettingsCameraItem
            icon={<Icon name="videoOutline" size={18} color={theme.colors.interactive.controls.default_midground} />}
            title="Camera details"
            onPress={handleCameraDetailsPress}
          />
        </FeatureGate>
      </View>
    </ScrollView>
  );

  // Mobile layout
  return (
    <>
      <Stack.Screen options={headerOptions} />
      <DataStateView
        data={effectiveCamera}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        loadingMessage="Loading camera..."
        errorTitle="Camera not found"
        errorMessage={String(error) || "The requested camera could not be found"}
      >
        {renderCameraContent}
      </DataStateView>

      <ScrollableBottomSheet bottomSheetRef={armDisarmBottomSheetRef}>
        <ArmDisarmPanelContent
          mode={bottomSheetMode}
          targetAction={bottomSheetTargetAction}
          activeOverride={bottomSheetActiveOverride}
          timezone={getTimezoneFromAlarmDetail(alarmDetail) || "UTC"}
          onConfirm={(exception) => {
            handleArmDisarmConfirm(exception);
            armDisarmBottomSheetRef.current?.dismiss();
          }}
          renderTimePicker={({ isVisible, hour, minute, onConfirm, onCancel }) => (
            <TimePickerWith24Hour
              isVisible={isVisible}
              hour={hour}
              minute={minute}
              onConfirm={onConfirm}
              onCancel={onCancel}
            />
          )}
        />
      </ScrollableBottomSheet>
      <SilenceAlarmBottomSheet
        bottomSheetRef={silenceAlarmBottomSheetRef}
        isAlreadySilenced={isSilenced}
        onConfirm={handleSilenceConfirm}
      />
    </>
  );
}
